import { ConfigProvider, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useState } from 'react';
import {
  CLEAR_BUTTON_LABEL,
  DATE_FIELD_DAY_PLACEHOLDER,
  DATE_FIELD_MONTH_PLACEHOLDER,
  DATE_FIELD_SET_DAY_LABEL,
  DATE_FIELD_SET_MONTH_ONLY_LABEL,
} from 'constants/uiConstants';
import { useCalendarDayValue } from 'hooks/useCalendarDayValue';
import { useDatePickerPanel } from 'hooks/useDatePickerPanel';
import { toCalendarDateIso } from 'utils/inputDraftPatches';

import { parseTypedDate } from './parseTypedDate';

type DateFieldMode = 'day' | 'month';

const DAY_FORMAT = 'MMMM DD, YYYY';
const MONTH_FORMAT = 'MMMM, YYYY';

const cssVar = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export interface DateFieldEditorProps {
  value: string | null;
  hasDay: boolean;
  onCommit: (value: string | null, hasDay: boolean) => void;
  onClose: () => void;
  className?: string;
  // Panel month to open to when `value` is unset — e.g. the input document's
  // Start Date, so an empty Task End Date doesn't default to today's month.
  fallbackDate?: string | null;
}

const DateFieldEditor: React.FC<DateFieldEditorProps> = ({
  value,
  hasDay,
  onCommit,
  onClose,
  className,
  fallbackDate,
}) => {
  const [mode, setMode] = useState<DateFieldMode>(hasDay ? 'day' : 'month');
  const dayValue = useCalendarDayValue(value);
  const fallbackDayValue = useCalendarDayValue(fallbackDate);
  const panel = useDatePickerPanel(dayValue, fallbackDayValue);
  // A typed date without a year ("1/1") resolves on or after the plan's start
  // date (`fallbackDate`), not in the current year.
  const typedDateAnchor = fallbackDayValue ?? dayValue ?? dayjs();

  const commit = (date: Dayjs | null, withDay: boolean) => {
    onCommit(
      date ? toCalendarDateIso(date.toDate()) : null,
      !!date && withDay
    );
    onClose();
  };

  // A month click commits straight away, the same as a day click — there is
  // no Confirm step (OP-2534).
  const handleCellClick = (date: Dayjs) => {
    if (mode === 'day') {
      commit(date, true);
    } else {
      commit(date.startOf('month'), false);
    }
  };

  // Typed entry, day before month (OP-2534). Enter or Tab commits a date
  // parseTypedDate recognises; anything else is left to the picker, so an
  // unrecognised entry can be corrected rather than silently dropped.
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    preventDefault: () => void
  ) => {
    if (event.key !== 'Enter' && event.key !== 'Tab') return;
    if (!(event.target instanceof HTMLInputElement)) return;
    const typed = parseTypedDate(event.target.value, typedDateAnchor);
    if (!typed) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      preventDefault();
    }
    commit(typed.date, typed.hasDay);
  };

  return (
    <ConfigProvider
      theme={{ token: { colorPrimary: cssVar('--color-success-dark') } }}
    >
      <DatePicker
        className={className}
        autoFocus
        open
        variant="borderless"
        picker={mode === 'day' ? 'date' : 'month'}
        needConfirm={false}
        format={mode === 'day' ? DAY_FORMAT : MONTH_FORMAT}
        placeholder={
          mode === 'day'
            ? DATE_FIELD_DAY_PLACEHOLDER
            : DATE_FIELD_MONTH_PLACEHOLDER
        }
        value={dayValue}
        pickerValue={panel.pickerValue}
        onPickerValueChange={panel.onPickerValueChange}
        onCalendarChange={panel.onCalendarChange}
        onOpenChange={(open) => {
          panel.onOpenChange(open);
          if (!open) onClose();
        }}
        onKeyDown={handleKeyDown}
        allowClear={false}
        suffixIcon={null}
        cellRender={(current, info) => {
          if (info.type !== (mode === 'day' ? 'date' : 'month')) {
            return info.originNode;
          }
          return (
            <span
              className="contents"
              onClick={() => handleCellClick(current as Dayjs)}
            >
              {info.originNode}
            </span>
          );
        }}
        renderExtraFooter={() => (
          <div className="my-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="rounded-[var(--radius-button)] bg-primary-dark px-3 py-1 text-sm font-semibold leading-normal text-white"
              onClick={() => setMode(mode === 'day' ? 'month' : 'day')}
            >
              {mode === 'day'
                ? DATE_FIELD_SET_MONTH_ONLY_LABEL
                : DATE_FIELD_SET_DAY_LABEL}
            </button>
            {value && (
              <button
                type="button"
                className="rounded-[var(--radius-button)] px-3 py-1 text-sm font-semibold leading-normal text-gray-dark hover:bg-rowHover"
                onClick={() => commit(null, false)}
              >
                {CLEAR_BUTTON_LABEL}
              </button>
            )}
          </div>
        )}
      />
    </ConfigProvider>
  );
};

export default DateFieldEditor;
